import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './KnowledgeBaseLaunchPage.module.css';

declare global {
  interface Window {
    Coveo?: any;
    hostedSearchPageLoading?: Promise<any>;
  }

  // Allow the hosted-search-page custom element in JSX
  namespace JSX {
    interface IntrinsicElements {
      'hosted-search-page': React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement>;
    }
  }
}

const loadScript = (src: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load script ${src}`));
    document.head.appendChild(script);
  });
};

const KnowledgeBaseLaunchPage: React.FC = () => {
  const navigate = useNavigate();
  const [justInItems, setJustInItems] = React.useState<Array<{ title: string; category: string }>>([
    { title: 'Troubleshoot cloud storage access', category: 'Troubleshooting' },
    { title: 'Manage user permissions effectively', category: 'Best Practices' },
    { title: 'Resolve backup failures', category: 'Troubleshooting' },
    { title: 'Monitor data security events', category: 'Best Practices' },
    { title: 'Analyze data compliance risks', category: 'Security' },
    { title: 'Recover from ransomware attacks', category: 'Disaster Recovery' }
  ]);

  const handleJustInItemClick = (item: typeof justInItems[0]) => {
    // Search for the item in Coveo
    const searchBox = document.querySelector('.CoveoSearchBox input') as HTMLInputElement;
    if (searchBox) {
      searchBox.value = item.title;
      searchBox.dispatchEvent(new Event('change', { bubbles: true }));
      searchBox.dispatchEvent(new KeyboardEvent('keydown', { key: 'Enter', bubbles: true }));
    }
  };

  useEffect(() => {
    const init = async () => {
      try {
        // Load Coveo scripts
        console.log('[KBLaunch] Loading Coveo scripts...');
        await loadScript('https://static.cloud.coveo.com/searchui/v2.10091/js/CoveoJsSearch.Lazy.js');
        console.log('[KBLaunch] CoveoJsSearch.Lazy.js loaded');
        
        await loadScript('https://static.cloud.coveo.com/searchui/v2.10091/js/templates/templates.js');
        console.log('[KBLaunch] templates.js loaded');
        
        await loadScript('https://unpkg.com/@coveops/hosted-search-page@latest/dist/index.min.js');
        console.log('[KBLaunch] hosted-search-page loaded');
        
        await loadScript('https://unpkg.com/@coveops/context-dropdown@latest/dist/index.min.js');
        console.log('[KBLaunch] context-dropdown loaded');

        // Wait a bit for scripts to initialize (reduced from 500ms)
        await new Promise(resolve => setTimeout(resolve, 200));

        const configResp = await fetch('/api/Knowledge/CoveoConfig', { credentials: 'include' });
        let config: any = {};
        
        if (!configResp.ok) {
          console.warn('[KBLaunch] Failed to load Coveo config from backend, using fallback');
        } else {
          config = await configResp.json();
          console.log('[KBLaunch] Coveo config received:', config);
        }

        if (!config.useCoveo) {
          console.warn('[KBLaunch] Coveo is not enabled in config, using hardcoded fallback for testing');
          config = {
            useCoveo: true,
            organizationId: 'commvaultsystemsincproduction5963mr9d',
            apiKey: 'xx2842b3a7-14e4-418c-bc7f-ce49a04debcc',
            searchPageId: 'c42e24fc-a13b-4ad3-9a38-76a79a5a68e3',
            searchHub: 'MA_Search_Core',
            userToken: 'eyJhbGciOiJIUzI1NiJ9.eyJmaWx0ZXIiOiJAY29tbW9ucHJvZHVjdD09KFwiQ29tbXZhdWx0XCIsXCJNZXRhbGxpY1wiLFwiSGVkdmlnXCIpIiwic2VhcmNoSHViIjoiTUFfU2VhcmNoX0NvcmUiLCJ2OCI6dHJ1ZSwidG9rZW5JZCI6InZkcHkybXlrYW1rY3NxYjRueXZjdGtramFlIiwib3JnYW5pemF0aW9uIjoiY29tbXZhdWx0c3lzdGVtc2luY3Byb2R1Y3Rpb241OTYzbXI5ZCIsInVzZXJJZHMiOlt7InR5cGUiOiJVc2VyIiwibmFtZSI6InBhcnRuZXJAY29tbXZhdWx0LmNvbSIsInByb3ZpZGVyIjoiRW1haWwgU2VjdXJpdHkgUHJvdmlkZXIifV0sInJvbGVzIjpbInF1ZXJ5RXhlY3V0b3IiXSwiaXNzIjoiU2VhcmNoQXBpIiwidXNlckRpc3BsYXlOYW1lIjoiUmFqZXNoIE5hdHRlIiwiZXhwIjoxNzY1NjM0ODU2LCJpYXQiOjE3NjU1NDg0NTZ9.BgORKFOUNrHbfvd38LxF3-C2AmnOe-GjIfygm42mSUs',
            isCloudSaaS: false,
            defaultVersion: '11.36'
          };
        }

        // Wait for custom element to be defined (optimized - check more frequently)
        let customElementDefined = false;
        let retries = 0;
        const maxRetries = 10; // Reduced from 20
        while (retries < maxRetries && !customElementDefined) {
          customElementDefined = customElements.get('hosted-search-page') !== undefined;
          if (!customElementDefined) {
            console.log(`[KBLaunch] Waiting for hosted-search-page custom element to be defined... (${retries + 1}/${maxRetries})`);
            await new Promise(resolve => setTimeout(resolve, 100)); // Reduced from 300ms
            retries++;
          }
        }

        if (!customElementDefined) {
          throw new Error('hosted-search-page custom element was not defined after loading scripts. Check if the @coveops/hosted-search-page script loaded correctly.');
        }

        console.log('[KBLaunch] hosted-search-page custom element is defined');

        // Wait for hosted search page element to be ready
        const hspEl = document.getElementById('hsp') as any;
        if (!hspEl) {
          throw new Error('Hosted Search Page container element (#hsp) not found in DOM');
        }

        // Wait for the configure method to be available (optimized)
        retries = 0;
        while (retries < maxRetries && (!hspEl.configure || typeof hspEl.configure !== 'function')) {
          console.log(`[KBLaunch] Waiting for hosted search page configure method... (${retries + 1}/${maxRetries})`);
          await new Promise(resolve => setTimeout(resolve, 100)); // Reduced from 300ms
          retries++;
        }

        if (!hspEl.configure || typeof hspEl.configure !== 'function') {
          console.error('[KBLaunch] Hosted search page element state:', {
            hspEl: hspEl,
            tagName: hspEl.tagName,
            hasConfigure: !!hspEl.configure,
            configureType: typeof hspEl.configure,
            customElementDefined: customElements.get('hosted-search-page') !== undefined,
            windowKeys: Object.keys(window).filter(k => k.toLowerCase().includes('coveo') || k.toLowerCase().includes('hosted'))
          });
          throw new Error('Hosted Search Page element configure method not available. The element may not have initialized correctly.');
        }
        
        console.log('[KBLaunch] Hosted search page element is ready with configure method');

        // Configure hosted search page (matches original implementation)
        // The pageId is a GUID that maps to the KB_Search page
        // The URL will show KB_Search as the page name, but the API needs the GUID
        console.log('[KBLaunch] Configuring hosted search page with pageId:', config.searchPageId);
        console.log('[KBLaunch] Config:', { 
          orgId: config.organizationId, 
          apiKey: config.apiKey?.substring(0, 10) + '...', 
          pageId: config.searchPageId,
          searchHub: config.searchHub
        });
        
        // Configure hosted search page
        // IMPORTANT: The hosted search page component requires a GUID for API calls
        // The page name Kb_search_New_Customer_Portal exists, but API needs the GUID
        // Use the GUID from config (should be updated to Kb_search_New_Customer_Portal's GUID)
        if (!config.searchPageId) {
          throw new Error('SearchPageId (GUID) is required but was not provided in config');
        }
        
        console.log('[KBLaunch] Using GUID for pageId (required for API):', config.searchPageId);
        console.log('[KBLaunch] This GUID should map to Kb_search_New_Customer_Portal page');
        console.log('[KBLaunch] The URL will show the page name after loading');

        window.hostedSearchPageLoading = hspEl.configure({
          orgId: config.organizationId,
          apiKey: config.apiKey,
          pageId: config.searchPageId // MUST use GUID for API calls
        });
        
        // After the page loads, check if we can update the URL to show KB_Search
        // The hosted search page might load in an iframe, and we can try to update the URL
        (window.hostedSearchPageLoading || Promise.resolve()).then(() => {
          setTimeout(() => {
            // Try to find and update the iframe URL if the hosted search page uses one
            const iframe = hspEl.querySelector('iframe');
            if (iframe && iframe.contentWindow) {
              try {
                const currentUrl = iframe.contentWindow.location.href;
                console.log('[KBLaunch] Current iframe URL:', currentUrl);
                // If the URL contains the organization and pages, try to update it
                if (currentUrl.includes('/pages/')) {
                  const urlParts = currentUrl.split('/pages/');
                  if (urlParts.length > 1) {
                    const afterPages = urlParts[1].split('/')[0];
                    // Only update if it's not already KB_Search
                    if (afterPages !== 'KB_Search' && afterPages !== 'kb_search') {
                      const newUrl = currentUrl.replace(/\/pages\/[^\/]+/, `/pages/${config.organizationId}/KB_Search`);
                      try {
                        iframe.contentWindow.history.replaceState({}, '', newUrl);
                        console.log('[KBLaunch] Updated iframe URL to show KB_Search');
                      } catch (e) {
                        console.warn('[KBLaunch] Could not update iframe URL (may be cross-origin):', e);
                      }
                    }
                  }
                }
              } catch (e) {
                console.warn('[KBLaunch] Could not access iframe URL (cross-origin):', e);
              }
            }
          }, 2000);
        });

        // Hide context dropdown initially using jQuery (matches original)
        if ((window as any).jQuery) {
          (window as any).jQuery(".CoveoContextDropdown").css("visibility", "hidden");
        } else {
          const contextDropdown = document.querySelector('.CoveoContextDropdown') as HTMLElement;
          if (contextDropdown) {
            contextDropdown.style.visibility = 'hidden';
          }
        }

        // Fetch release groups in parallel with hosted search page loading (optimized)
        const releaseGroupsPromise = !config.isCloudSaaS 
          ? fetch('/api/Release/Groups', { credentials: 'include' })
              .then(relResp => relResp.ok ? relResp.json() : [])
              .then(relJson => Array.isArray(relJson)
                ? relJson.map((item: any) => ({
                    name: item.Label,
                    options: (item.Releases || []).map((release: any) => ({
                      label: release.Display,
                      value: release.Value
                    }))
                  }))
                : [])
          : Promise.resolve([]);

        // Wait for hosted search page to fully load (critical - matches original)
        // Run in parallel with release groups fetch for better performance
        console.log('[KBLaunch] Waiting for hosted search page to load...');
        const [releaseGroups] = await Promise.all([
          releaseGroupsPromise,
          window.hostedSearchPageLoading || Promise.resolve()
        ]);
        console.log('[KBLaunch] Hosted search page loading promise resolved');

        // Additional wait to ensure hosted search page content is fully rendered (optimized)
        // The hosted search page needs time to render its internal structure
        console.log('[KBLaunch] Waiting for hosted search page content to render...');
        await new Promise(resolve => setTimeout(resolve, 1000)); // Reduced from 2000ms
        
        // Check if searchPage property exists - wait up to 3 seconds (optimized)
        let searchPageAvailable = !!(hspEl as any).searchPage;
        if (!searchPageAvailable) {
          console.warn('[KBLaunch] searchPage property not yet available, waiting more...');
          for (let i = 0; i < 6; i++) { // Reduced from 10 to 6
            await new Promise(resolve => setTimeout(resolve, 250)); // Reduced from 500ms
            if ((hspEl as any).searchPage) {
              searchPageAvailable = true;
              console.log('[KBLaunch] searchPage property is now available');
              break;
            }
          }
          if (!searchPageAvailable) {
            console.warn('[KBLaunch] searchPage property still not available after extended wait');
            console.warn('[KBLaunch] Hosted search page may still work - it might initialize differently');
          }
        } else {
          console.log('[KBLaunch] searchPage property is available');
        }

        // Set up search endpoint with token (matches original)
        const token = config.userToken;
        (window as any).Coveo.SearchEndpoint.endpoints['default'] = new (window as any).Coveo.SearchEndpoint({
          restUri: 'https://platform.cloud.coveo.com/rest/search',
          accessToken: token,
          queryStringArguments: { organizationId: config.organizationId }
        });

        // Get search interface from hosted search page (matches original - uses searchPage property)
        // Wait for searchPage property to be available (optimized)
        let searchInterface: Element | null = null;
        retries = 0;
        const maxSearchInterfaceRetries = 12; // Reduced from 20
        while (retries < maxSearchInterfaceRetries && !searchInterface) {
          // Try multiple ways to find the search interface
          if ((hspEl as any).searchPage) {
            searchInterface = (hspEl as any).searchPage.querySelector('.CoveoSearchInterface');
            if (searchInterface) {
              console.log(`[KBLaunch] Found search interface via searchPage property`);
              break;
            }
          }
          
          if (!searchInterface) {
            // Try direct query from hsp element
            searchInterface = hspEl.querySelector('.CoveoSearchInterface');
            if (searchInterface) {
              console.log(`[KBLaunch] Found search interface via hsp element`);
              break;
            }
          }
          
          if (!searchInterface) {
            // Try querying from document
            searchInterface = document.querySelector('#hsp .CoveoSearchInterface');
            if (searchInterface) {
              console.log(`[KBLaunch] Found search interface via document query`);
              break;
            }
          }
          
          if (!searchInterface) {
            // Try with shadow DOM if it exists
            if ((hspEl as any).shadowRoot) {
              searchInterface = (hspEl as any).shadowRoot.querySelector('.CoveoSearchInterface');
              if (searchInterface) {
                console.log(`[KBLaunch] Found search interface via shadowRoot`);
                break;
              }
            }
          }
          
          if (!searchInterface) {
            // Debug: log what we can find
            const allElements = hspEl.querySelectorAll('*');
            const coveoElements = Array.from(allElements).filter((el: Element) => 
              el.className && el.className.toString().includes('Coveo')
            );
            
            // Also check inside searchPage if it exists
            let searchPageElements = 0;
            if ((hspEl as any).searchPage) {
              const searchPageAll = (hspEl as any).searchPage.querySelectorAll('*');
              searchPageElements = searchPageAll.length;
            }
            
            console.log(`[KBLaunch] Attempt ${retries + 1}/${maxSearchInterfaceRetries}: Found ${coveoElements.length} Coveo elements in hsp, ${searchPageElements} elements in searchPage, waiting...`);
            console.log(`[KBLaunch] hspEl structure:`, {
              tagName: hspEl.tagName,
              hasSearchPage: !!(hspEl as any).searchPage,
              searchPageType: (hspEl as any).searchPage ? typeof (hspEl as any).searchPage : 'none',
              hasShadowRoot: !!(hspEl as any).shadowRoot,
              childrenCount: hspEl.children.length,
              innerHTMLLength: hspEl.innerHTML.length,
              firstChildTag: hspEl.firstElementChild?.tagName
            });
            
            // Try to find any element with CoveoSearchInterface class anywhere
            const anyCoveoInterface = document.querySelector('.CoveoSearchInterface');
            if (anyCoveoInterface) {
              console.log('[KBLaunch] Found .CoveoSearchInterface somewhere in document, but not in expected location');
            }
            
            await new Promise(resolve => setTimeout(resolve, 250)); // Reduced from 500ms
            retries++;
          }
        }
        
        if (!searchInterface) {
          // Final attempt - try to find any Coveo element and log structure
          console.error('[KBLaunch] Search interface not found. Debug info:');
          console.error('hspEl:', hspEl);
          console.error('hspEl.searchPage:', (hspEl as any).searchPage);
          console.error('hspEl.innerHTML (first 1000 chars):', hspEl.innerHTML.substring(0, 1000));
          const allCoveoElements = document.querySelectorAll('[class*="Coveo"]');
          console.error('All Coveo elements in document:', allCoveoElements.length);
          
          // Try one more time after a shorter wait (optimized)
          console.log('[KBLaunch] Waiting additional 1 second for hosted search page to fully render...');
          await new Promise(resolve => setTimeout(resolve, 1000)); // Reduced from 3000ms
          
          // Try all methods again
          if ((hspEl as any).searchPage) {
            searchInterface = (hspEl as any).searchPage.querySelector('.CoveoSearchInterface');
          }
          if (!searchInterface) {
            searchInterface = hspEl.querySelector('.CoveoSearchInterface');
          }
          if (!searchInterface) {
            searchInterface = document.querySelector('#hsp .CoveoSearchInterface');
          }
          if (!searchInterface && (hspEl as any).shadowRoot) {
            searchInterface = (hspEl as any).shadowRoot.querySelector('.CoveoSearchInterface');
          }
          
          if (!searchInterface) {
            // Last resort: try to find any element that might be the search interface
            const possibleInterfaces = document.querySelectorAll('[class*="SearchInterface"], [class*="search-interface"], .CoveoSearchInterface, [id*="search"]');
            console.error('[KBLaunch] Found possible search interface elements:', possibleInterfaces.length);
            if (possibleInterfaces.length > 0) {
              console.error('[KBLaunch] Possible interfaces:', Array.from(possibleInterfaces).map(el => ({
                tagName: el.tagName,
                className: el.className,
                id: el.id
              })));
            }
            
            // If hosted search page is working, we can still proceed without explicit initialization
            // The hosted search page might handle its own initialization
            console.warn('[KBLaunch] Search interface not found, but hosted search page may still work');
            console.warn('[KBLaunch] Attempting to proceed without explicit Coveo.init()');
            
            // Don't throw error - let the hosted search page work on its own
            // Just set up click interception on the hsp element itself
            searchInterface = hspEl; // Use the hsp element as fallback
          }
        }
        
        if (searchInterface) {
          console.log('[KBLaunch] Search interface found:', searchInterface);
        } else {
          console.warn('[KBLaunch] Proceeding without explicit search interface - hosted search page may handle initialization');
        }

        // Function to extract article ID from URI
        const extractArticleId = (uri: string | null): string | null => {
          if (!uri) return null;
          
          // Pattern 1: /article/62389 or /Article/62389 or /Details/62389
          let match = uri.match(/\/(?:article|Article|Details|KB\/Article\/Details)\/(\d+)/i);
          if (match) return match[1];
          
          // Pattern 2: Extract numeric ID from path segments
          const parts = uri.split('/').filter(p => p);
          const idPart = parts.find(p => /^\d+$/.test(p));
          if (idPart) return idPart;
          
          // Pattern 3: Extract from query params (e.g., ?id=62389 or ?articleId=62389)
          try {
            const url = new URL(uri, window.location.origin);
            const id = url.searchParams.get('id') || url.searchParams.get('articleId');
            if (id && /^\d+$/.test(id)) return id;
          } catch {
            // Not a valid URL, continue
          }
          
          return null;
        };

        // Function to intercept and handle article clicks
        const setupClickInterception = () => {
          // Try to find search interface, but also set up on hsp element as fallback
          let searchInterfaceEl: Element | null = document.querySelector('#hsp .CoveoSearchInterface');
          if (!searchInterfaceEl && (hspEl as any).searchPage) {
            searchInterfaceEl = (hspEl as any).searchPage.querySelector('.CoveoSearchInterface');
          }
          if (!searchInterfaceEl) {
            // Use hsp element as fallback - clicks will bubble up
            searchInterfaceEl = hspEl;
            console.log('[KBLaunch] Using hsp element for click interception (fallback)');
          } else {
            console.log('[KBLaunch] Using search interface element for click interception');
          }

          // Method 1: Use Coveo's built-in result click events
          if ((window as any).Coveo) {
            try {
              // Listen for result open events using Coveo's event system
              (window as any).Coveo.bind.onSearchInterfaceReady(searchInterfaceEl, () => {
                // Override result link clicks using Coveo's query events
                (window as any).Coveo.bind.onQuerySuccess(searchInterfaceEl, () => {
                  // After results are rendered, intercept clicks
                  setTimeout(() => {
                    const resultLinks = searchInterfaceEl.querySelectorAll(
                      '.CoveoResultLink, .CoveoResult a, a[data-uri], a[href*="/article/"], a[href*="/Article/"], a[href*="/Details/"]'
                    );
                    
                    resultLinks.forEach((link: Element) => {
                      const href = link.getAttribute('href') || link.getAttribute('data-uri');
                      if (href) {
                        const articleId = extractArticleId(href);
                        if (articleId) {
                          // Replace the link's click handler
                          link.addEventListener('click', (e: Event) => {
                            e.preventDefault();
                            e.stopPropagation();
                            console.log('[KBLaunch] Intercepted article click, navigating to:', articleId);
                            navigate(`/knowledge/article/${articleId}`);
                            return false;
                          }, true);
                        }
                      }
                    });
                  }, 100);
                });
              });
            } catch (err) {
              console.warn('[KBLaunch] Could not set up Coveo click interception:', err);
            }
          }

          // Method 2: Global click listener as fallback (catches all clicks)
          const globalClickHandler = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (!target) return;

            // Check if click is on a result link or within a result
            const resultLink = target.closest('.CoveoResultLink, .CoveoResult a, a[data-uri]') as HTMLElement;
            const resultElement = target.closest('.CoveoResult') as HTMLElement;
            
            if (resultLink) {
              const href = resultLink.getAttribute('href') || resultLink.getAttribute('data-uri');
              if (href) {
                const articleId = extractArticleId(href);
                if (articleId) {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log('[KBLaunch] Intercepted article click (global), navigating to:', articleId);
                  navigate(`/knowledge/article/${articleId}`);
                  return false;
                }
              }
            } else if (resultElement) {
              // Try to get article ID from result data attributes
              const resultData = (resultElement as any).result;
              if (resultData) {
                const raw = resultData.raw;
                if (raw?.commvaultalexandriaitemid) {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log('[KBLaunch] Intercepted article click from result data, navigating to:', raw.commvaultalexandriaitemid);
                  navigate(`/knowledge/article/${raw.commvaultalexandriaitemid}`);
                  return false;
                }
                
                // Try to get from clickUri
                const clickUri = resultData.clickUri;
                if (clickUri) {
                  const articleId = extractArticleId(clickUri);
                  if (articleId) {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log('[KBLaunch] Intercepted article click from clickUri, navigating to:', articleId);
                    navigate(`/knowledge/article/${articleId}`);
                    return false;
                  }
                }
              }
            }
          };

          // Use capture phase to intercept before Coveo handles it
          searchInterfaceEl.addEventListener('click', globalClickHandler, true);
        };

        // Initialize Coveo with search interface (matches original)
        // Only initialize if we have a valid search interface (not the fallback hsp element)
        if (searchInterface && searchInterface !== hspEl && searchInterface.classList?.contains !== undefined) {
          const initOptions: any = {
            Analytics: { searchHub: config.searchHub }
          };

          // Only add ContextDropdown if not Cloud SaaS and we have release groups
          if (!config.isCloudSaaS && releaseGroups.length > 0) {
            initOptions.ContextDropdown = {
            values: releaseGroups,
            isGrouped: true,
            triggerSearch: true,
            contextKey: 'version',
            initialChoice: config.defaultVersion || undefined
            };
          }

          console.log('[KBLaunch] Initializing Coveo with options:', initOptions);
          try {
            (window as any).Coveo.init(searchInterface, initOptions);
            console.log('[KBLaunch] Coveo.init() completed successfully');

            // Wait a bit for Coveo to initialize components (filters, dropdowns, etc.) (optimized)
            await new Promise(resolve => setTimeout(resolve, 200)); // Reduced from 500ms

            // Show context dropdown after initialization using jQuery (matches original)
            if ((window as any).jQuery) {
              (window as any).jQuery(".CoveoContextDropdown").css("visibility", "visible");
              console.log('[KBLaunch] Context dropdown made visible using jQuery');
            } else {
              const contextDropdownFinal = document.querySelector('.CoveoContextDropdown') as HTMLElement;
              if (contextDropdownFinal) {
                contextDropdownFinal.style.visibility = 'visible';
                console.log('[KBLaunch] Context dropdown made visible using vanilla JS');
              } else {
                console.warn('[KBLaunch] Context dropdown element not found - it may not be rendered yet');
              }
            }
            
            console.log('[KBLaunch] Coveo initialization complete');
          } catch (initError) {
            console.warn('[KBLaunch] Coveo.init() failed, but hosted search page may still work:', initError);
          }
        } else {
          console.log('[KBLaunch] Skipping Coveo.init() - hosted search page will handle its own initialization');
          console.log('[KBLaunch] The hosted search page component should work independently');
          
          // Still try to show context dropdown if it exists
          setTimeout(() => {
            if ((window as any).jQuery) {
              (window as any).jQuery(".CoveoContextDropdown").css("visibility", "visible");
            } else {
              const contextDropdownFinal = document.querySelector('.CoveoContextDropdown') as HTMLElement;
              if (contextDropdownFinal) {
                contextDropdownFinal.style.visibility = 'visible';
              }
            }
          }, 2000);
        }

        // Set up click interception after Coveo initializes (optimized)
        setTimeout(() => {
          setupClickInterception();
        }, 500); // Reduced from 1500ms
      } catch (err) {
        console.error('[KBLaunch] Initialization error:', err);
        // Display error message on page
        const errorDiv = document.createElement('div');
        errorDiv.style.cssText = 'padding: 20px; background: #fee; border: 1px solid #fcc; color: #c00; margin: 20px;';
        errorDiv.innerHTML = `<h3>Error Loading Knowledge Base</h3><p>${err instanceof Error ? err.message : String(err)}</p><p>Check the browser console for more details.</p>`;
        const container = document.querySelector(`.${styles.kbPageWhite}`);
        if (container) {
          container.innerHTML = '';
          container.appendChild(errorDiv);
        }
      }
    };

    init();
  }, [navigate]);

  return (
    <div className={styles.kbPageWhite}>
      <header className={styles.topBar}>
        <div className={styles.brand}>Commvault</div>
        <div className={styles.topActions}>
          <span className={styles.topLink}>Search</span>
          <span className={styles.topLink}>Collections</span>
          <span className={styles.topLink}>Saved</span>
          <span className={styles.topLink}>Support</span>
        </div>
      </header>

      <div className={styles.pageBody}>
        <div className={styles.hspWrapper}>
          <hosted-search-page
            id="hsp"
            style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}
          ></hosted-search-page>
        </div>

        <aside className={styles.justInPanel}>
          <div className={styles.justInHeader}>Just in</div>
          <p className={styles.justInSubhead}>Take a moment to see the latest knowledge released</p>
          <div className={styles.justInList}>
            {justInItems.map((item) => (
              <div
                key={item.title}
                className={styles.justInItem}
                onClick={() => handleJustInItemClick(item)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handleJustInItemClick(item);
                  }
                }}
              >
                <div className={styles.justInItemTitle}>{item.title}</div>
                <div className={styles.justInItemCategory}>{item.category}</div>
              </div>
            ))}
          </div>
        </aside>
      </div>
    </div>
  );
};

export default KnowledgeBaseLaunchPage;
